import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Checkbox, Modal, ModalHeader, ModalBody, ModalFooter, Label, TextInput, Select, Textarea } from 'flowbite-react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiBarChart2, FiChevronDown, FiDownload, FiExternalLink, FiGrid, FiLayout, FiPlay, FiPlus, FiTarget, FiTrash2 } from 'react-icons/fi';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { useAsync } from 'react-async-hook';
import {
  Node,
  Edge,
  Connection,
} from '@xyflow/react';
import * as sched from 'sched';
import EditableTitle from '../../components/EditableTitle';
import ErrorAlert from '../../components/common/ErrorAlert';
import { useAuth } from '../../hooks/useAuth';
import WorkflowStepConditionForm, {
  WorkflowStepConditionFormData,
  formDataToWorkflowStepCondition,
  workflowStepConditionToFormData
} from '../../components/forms/WorkflowStepConditionForm';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import WorkflowGraph, { getNodeDisplayContext, WorkflowGraphNodeData } from '../../components/workflow/WorkflowGraph';
import { FuzzyGanttChart, type FuzzyScheduleResult } from '../../components/charts/FuzzyGanttChart';

type ModalConfig = null | { type: "create" } | { type: "edit", stepId: string };

// Validation schemas
const subjectTypes = z.enum(['Users', 'Everyone', 'Team', 'Parameter']);

const stepSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string(),
  condition: z.object({
    type: z.enum(['Immediate', 'Manual', 'TimeElapsed', 'TimeReached', 'WorkflowStateQuery', 'Branch']),
    actorType: subjectTypes.optional(),
    actorId: z.string().optional(),
    durationSeconds: z.number().optional(),
    targetTime: z.string().optional(),
    stateQuery: z.string().optional(),
    branchOptions: z.record(z.string()).optional(),
    multiSelect: z.boolean().optional(),
    deciderType: subjectTypes.optional(),
    deciderId: z.string().optional(),
  }),
});

// ─── Unschedulable Panel ─────────────────────────────────────────────────────

interface UnschedulablePanelProps {
  unschedulable: Record<string, { label: string; reason: string }>;
  meta: FuzzyScheduleResult['meta'];
  templateState: sched.WorkflowTemplateState | null;
  onFixEstimate: (stepId: string, estimate: sched.BoundedWorkEstimate) => Promise<void>;
  onEditStep: (stepId: string) => void;
  onPreDecide: (stepId: string, optionId: string) => void;
  discoveredBranches: { stepId: string; title: string; options: Record<string, string> }[];
}

const UnschedulablePanel: React.FC<UnschedulablePanelProps> = ({
  unschedulable, meta, templateState, onFixEstimate, onEditStep, onPreDecide, discoveredBranches,
}) => {
  const entries = Object.entries(unschedulable);
  const [estimates, setEstimates] = React.useState<Record<string, { min: string; expected: string; max: string }>>({});
  const [saving, setSaving] = React.useState<Set<string>>(new Set());

  const inputClass = "w-14 rounded border border-amber-300 dark:border-amber-600 bg-cm-bg-surface px-1.5 py-0.5 text-xs text-center";

  return (
    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-sm">
      <p className="font-medium text-amber-800 dark:text-amber-300 mb-2">
        {entries.length} step{entries.length !== 1 ? 's' : ''} could not be scheduled:
      </p>
      <div className="space-y-1.5">
        {entries.map(([nodeId, item]) => {
          const stepId = meta.node_to_step?.[nodeId] ?? nodeId;
          const step = stepId && templateState
            ? (templateState.steps as any[]).find(s => s.id === stepId)
            : null;
          const isMissingEstimate = item.reason === 'Missing work estimate';
          const isNoWorkers = item.reason === 'No workers are assigned to this step';
          const isBlockedByDecision = item.reason === 'Blocked by an unresolved decision';
          const est = estimates[stepId] ?? { min: '', expected: '', max: '' };

          return (
            <div key={stepId} className="flex items-center gap-3 py-1 border-t border-amber-200/50 dark:border-amber-700/50 first:border-t-0">
              <div className="flex-1 min-w-0">
                <span className="text-amber-800 dark:text-amber-300 font-medium">{item.label}</span>
                <span className="text-amber-600 dark:text-amber-400 ml-1.5">— {item.reason}</span>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1.5">
                {isMissingEstimate && step && (
                  <>
                    <input placeholder="min" value={est.min} onChange={e => setEstimates(prev => ({ ...prev, [stepId]: { ...est, min: e.target.value } }))} className={inputClass} />
                    <input placeholder="exp" value={est.expected} onChange={e => setEstimates(prev => ({ ...prev, [stepId]: { ...est, expected: e.target.value } }))} className={inputClass} />
                    <input placeholder="max" value={est.max} onChange={e => setEstimates(prev => ({ ...prev, [stepId]: { ...est, max: e.target.value } }))} className={inputClass} />
                    <button
                      disabled={saving.has(stepId) || !est.expected}
                      onClick={async () => {
                        const estimate: sched.BoundedWorkEstimate = {
                          min: est.min ? parseInt(est.min) : null,
                          expected: est.expected ? parseInt(est.expected) : null,
                          max: est.max ? parseInt(est.max) : null,
                        };
                        setSaving(prev => new Set(prev).add(stepId));
                        try {
                          await onFixEstimate(stepId, estimate);
                          setEstimates(prev => { const next = { ...prev }; delete next[stepId]; return next; });
                        } finally {
                          setSaving(prev => { const next = new Set(prev); next.delete(stepId); return next; });
                        }
                      }}
                      className="px-2 py-0.5 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                      Set
                    </button>
                  </>
                )}
                {isNoWorkers && stepId && (
                  <button
                    onClick={() => onEditStep(stepId)}
                    className="px-2 py-0.5 text-xs rounded bg-amber-600 text-white hover:bg-amber-700"
                  >
                    Edit step
                  </button>
                )}
                {isBlockedByDecision && (() => {
                  const branch = discoveredBranches.find(b => {
                    const branchNodeId = Object.entries(meta.node_to_step || {}).find(([, sid]) => sid === b.stepId)?.[0];
                    return branchNodeId !== undefined;
                  });
                  if (!branch) return null;
                  return (
                    <select
                      onChange={e => onPreDecide(branch.stepId, e.target.value)}
                      className="rounded border border-amber-300 dark:border-amber-600 bg-cm-bg-surface px-1.5 py-0.5 text-xs"
                    >
                      <option value="">Pre-decide...</option>
                      {Object.entries(branch.options).map(([optId, optLabel]) => (
                        <option key={optId} value={optId}>{optLabel}</option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ─── Table View ─────────────────────────────────────────────────────────────

interface WorkflowTableViewProps {
  templateState: sched.WorkflowTemplateState;
  onEditStep: (step: sched.WorkflowTemplateStep) => void;
  onDeleteStep: (stepId: string) => void;
  onSaveTitle: (stepId: string, title: string) => Promise<void>;
  onSaveEstimate: (stepId: string, estimate: sched.BoundedWorkEstimate) => Promise<void>;
  onAddDependency: (targetStepId: string, sourceStepId: string) => Promise<void>;
  onRemoveDependency: (targetStepId: string, sourceStepId: string) => Promise<void>;
}

type SortKey = 'title' | 'type' | 'assignment' | 'estimate' | 'deps';

const WorkflowTableView: React.FC<WorkflowTableViewProps> = ({
  templateState, onEditStep, onDeleteStep, onSaveTitle, onSaveEstimate,
  onAddDependency, onRemoveDependency,
}) => {
  const [sortBy, setSortBy] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingEstimate, setEditingEstimate] = useState<string | null>(null);
  const [estimateDraft, setEstimateDraft] = useState({ min: '', expected: '', max: '' });
  const [depsOpenFor, setDepsOpenFor] = useState<string | null>(null);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const stepType = (step: sched.WorkflowTemplateStep) => step.completion.type;

  const subjectLabel = (subject: sched.WorkflowAuthorizationSubject): string => {
    switch (subject.type) {
      case 'Everyone': return 'Everyone';
      case 'Users': return subject.ids.join(', ');
      case 'Team': return `Team: ${subject.id}`;
      case 'Parameter': return `Param: ${subject.key}`;
      default: return '—';
    }
  };

  const stepAssignment = (step: sched.WorkflowTemplateStep): string => {
    const c = step.completion;
    if (c.type === 'Manual') return subjectLabel(c.by);
    if (c.type === 'Branch') return subjectLabel(c.decider);
    return '—';
  };

  const stepEstimateValue = (step: sched.WorkflowTemplateStep): number | null => {
    const c = step.completion;
    if (c.type === 'Manual' && c.work_estimate?.expected != null) return c.work_estimate.expected;
    return null;
  };

  const depsForStep = (stepId: string) =>
    templateState.links.filter(l => l.target_step_id === stepId).map(l => l.source_step_id);

  const sorted = useMemo(() => {
    const steps = [...templateState.steps];
    const cmp = (a: sched.WorkflowTemplateStep, b: sched.WorkflowTemplateStep): number => {
      let result = 0;
      switch (sortBy) {
        case 'title': result = a.title.localeCompare(b.title); break;
        case 'type': result = stepType(a).localeCompare(stepType(b)); break;
        case 'assignment': result = stepAssignment(a).localeCompare(stepAssignment(b)); break;
        case 'estimate': result = (stepEstimateValue(a) ?? Infinity) - (stepEstimateValue(b) ?? Infinity); break;
        case 'deps': result = depsForStep(a.id).length - depsForStep(b.id).length; break;
      }
      return sortDir === 'asc' ? result : -result;
    };
    steps.sort(cmp);
    return steps;
  }, [templateState, sortBy, sortDir]);

  const sortIcon = (key: SortKey) => sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const thClass = "px-3 py-2 text-left text-xs font-medium text-cm-text-muted uppercase tracking-wider cursor-pointer hover:text-cm-text-primary select-none";
  const tdClass = "px-3 py-2 text-sm text-cm-text-primary";

  return (
    <div className="h-full overflow-auto p-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-cm-bg-elevated sticky top-0 z-10">
          <tr>
            <th className={thClass} onClick={() => toggleSort('title')}>Title{sortIcon('title')}</th>
            <th className={thClass} onClick={() => toggleSort('type')}>Type{sortIcon('type')}</th>
            <th className={thClass} onClick={() => toggleSort('assignment')}>Assignment{sortIcon('assignment')}</th>
            <th className={thClass} onClick={() => toggleSort('estimate')}>Estimate{sortIcon('estimate')}</th>
            <th className={thClass} onClick={() => toggleSort('deps')}>Deps{sortIcon('deps')}</th>
            <th className="px-3 py-2 w-20"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sorted.map(step => {
            const deps = depsForStep(step.id);
            const est = step.completion.type === 'Manual' ? step.completion.work_estimate : null;

            return (
              <tr key={step.id} className="hover:bg-cm-bg-elevated/50">
                {/* Title — inline editable */}
                <td className={tdClass}>
                  {editingTitle === step.id ? (
                    <input
                      autoFocus
                      className="w-full rounded border border-blue-400 bg-cm-bg-surface px-1.5 py-0.5 text-sm"
                      value={titleDraft}
                      onChange={e => setTitleDraft(e.target.value)}
                      onBlur={() => {
                        if (titleDraft.trim() && titleDraft !== step.title) onSaveTitle(step.id, titleDraft.trim());
                        setEditingTitle(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.currentTarget.blur(); }
                        if (e.key === 'Escape') setEditingTitle(null);
                      }}
                    />
                  ) : (
                    <span
                      className="cursor-pointer hover:underline"
                      onClick={() => { setEditingTitle(step.id); setTitleDraft(step.title); }}
                    >{step.title}</span>
                  )}
                </td>

                {/* Type — click to open edit modal */}
                <td className={`${tdClass} cursor-pointer`} onClick={() => onEditStep(step)}>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    step.completion.type === 'Manual' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30' :
                    step.completion.type === 'Branch' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                    step.completion.type === 'TimeElapsed' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-cm-bg-hover text-cm-text-primary'
                  }`}>{step.completion.type}</span>
                </td>

                {/* Assignment — click to open edit modal */}
                <td className={`${tdClass} cursor-pointer text-xs`} onClick={() => onEditStep(step)}>
                  {stepAssignment(step)}
                </td>

                {/* Estimate — inline editable for Manual steps */}
                <td className={tdClass}>
                  {step.completion.type === 'Manual' ? (
                    editingEstimate === step.id ? (
                      <div className="flex gap-1 items-center">
                        {['min', 'expected', 'max'].map(field => (
                          <input
                            key={field}
                            type="number"
                            placeholder={field.charAt(0).toUpperCase()}
                            className="w-14 rounded border border-blue-400 bg-cm-bg-surface px-1 py-0.5 text-xs text-center"
                            value={(estimateDraft as any)[field]}
                            onChange={e => setEstimateDraft(d => ({ ...d, [field]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                const estimate: sched.BoundedWorkEstimate = {
                                  min: estimateDraft.min ? Number(estimateDraft.min) : null,
                                  expected: estimateDraft.expected ? Number(estimateDraft.expected) : null,
                                  max: estimateDraft.max ? Number(estimateDraft.max) : null,
                                };
                                onSaveEstimate(step.id, estimate);
                                setEditingEstimate(null);
                              }
                              if (e.key === 'Escape') setEditingEstimate(null);
                            }}
                          />
                        ))}
                        <button
                          className="text-xs text-blue-600 hover:underline"
                          onClick={() => {
                            const estimate: sched.BoundedWorkEstimate = {
                              min: estimateDraft.min ? Number(estimateDraft.min) : null,
                              expected: estimateDraft.expected ? Number(estimateDraft.expected) : null,
                              max: estimateDraft.max ? Number(estimateDraft.max) : null,
                            };
                            onSaveEstimate(step.id, estimate);
                            setEditingEstimate(null);
                          }}
                        >Save</button>
                      </div>
                    ) : (
                      <span
                        className="cursor-pointer hover:underline text-xs"
                        onClick={() => {
                          setEditingEstimate(step.id);
                          setEstimateDraft({
                            min: est?.min?.toString() ?? '',
                            expected: est?.expected?.toString() ?? '',
                            max: est?.max?.toString() ?? '',
                          });
                        }}
                      >
                        {est?.expected != null
                          ? `${est.min ?? '?'}/${est.expected}/${est.max ?? '?'}`
                          : <span className="text-cm-text-muted italic">none</span>}
                      </span>
                    )
                  ) : (
                    <span className="text-cm-text-muted">—</span>
                  )}
                </td>

                {/* Dependencies — dropdown multi-select */}
                <td className={`${tdClass} relative`}>
                  <button
                    className="text-xs hover:underline"
                    onClick={() => setDepsOpenFor(depsOpenFor === step.id ? null : step.id)}
                  >
                    {deps.length > 0 ? `${deps.length} dep${deps.length !== 1 ? 's' : ''}` : <span className="text-cm-text-muted">none</span>}
                  </button>
                  {depsOpenFor === step.id && (
                    <div className="absolute left-0 top-full mt-1 z-50 w-64 max-h-48 overflow-y-auto bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-lg p-1">
                      {templateState.steps
                        .filter(s => s.id !== step.id)
                        .map(candidate => {
                          const isSelected = deps.includes(candidate.id);
                          return (
                            <label key={candidate.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-cm-bg-hover rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (isSelected) onRemoveDependency(step.id, candidate.id);
                                  else onAddDependency(step.id, candidate.id);
                                }}
                                className="rounded border-cm-border-subtle text-blue-600"
                              />
                              <span className="truncate">{candidate.title}</span>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </td>

                {/* Actions */}
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={() => onEditStep(step)} className="p-1 text-cm-text-muted hover:text-blue-600" title="Edit step">
                      <FiBarChart2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDeleteStep(step.id)} className="p-1 text-cm-text-muted hover:text-red-600" title="Delete step">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {templateState.steps.length === 0 && (
        <p className="text-center text-cm-text-muted py-8 text-sm">No steps yet. Click "Add Step" to get started.</p>
      )}
    </div>
  );
};

const WorkflowTemplateEditor: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { client, token } = useAuth();
  const { workspaceId, buildUrl } = useWorkspace();
  const { applyCommand, database } = useDatabase();

  const [nodes, setNodes] = useState<Node<WorkflowGraphNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [modalConfig, setModalConfig] = useState<ModalConfig>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleResult, setScheduleResult] = useState<FuzzyScheduleResult | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [viewMode, setViewMode] = useState<'graph' | 'table'>('graph');
  const [layoutGeneration, setLayoutGeneration] = useState(0);
  const prevStepCount = useRef<number | null>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [criticalPathData, setCriticalPathData] = useState<sched.CriticalPathResult | null>(null);
  const [criticalPathErrors, setCriticalPathErrors] = useState<sched.CriticalPathMissingEstimate[] | null>(null);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachSearch, setAttachSearch] = useState('');

  // Schedule options — loaded from localStorage on mount
  const storageKey = `schedule-opts:${templateId}`;
  const [poolWorkerCounts, setPoolWorkerCounts] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}').poolWorkerCounts ?? {}; } catch { return {}; }
  });
  const [unitsPerDay, setUnitsPerDay] = useState<number>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}').unitsPerDay ?? 1; } catch { return 1; }
  });
  const [preDecisions, setPreDecisions] = useState<Record<string, string[]>>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}').preDecisions ?? {}; } catch { return {}; }
  });

  // Persist schedule options to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ poolWorkerCounts, unitsPerDay, preDecisions }));
    } catch { /* ignore quota errors */ }
  }, [poolWorkerCounts, unitsPerDay, preDecisions, storageKey]);

  if (!templateId) {
    return <div>Template ID is required</div>;
  }

  const userProfilesResponse = useAsync(async () => {
    if (!client || !token || !workspaceId) return [];
    return (await sched.WrappedApiClient.list_workspace_user_profiles(client, token, workspaceId)).user_profiles;
  }, [client, token, workspaceId]);

  // Load template and events
  const templateResponse = useAsync(async () => {
    if (!client || !token || !templateId) return;
    return await sched.WrappedApiClient.get_workflow_template(client, token, templateId);
  }, [client, token, templateId]);

  const eventsResponse = useAsync(async () => {
    if (!client || !token || !templateId) return;
    return await sched.WrappedApiClient.list_workflow_template_events(client, token, templateId);
  }, [client, token, templateId]);

  // Build template state from events
  const templateState = useMemo(() => {
    if (!eventsResponse.result?.events) return null;
    return sched.build_workflow_template_state(eventsResponse.result.events);
  }, [eventsResponse.result]);



  // Discover work matcher pools from the template for pool config UI
  const discoveredPools = useMemo(() => {
    if (!templateState) return [];
    const pools: { label: string; displayName: string }[] = [];
    const seen = new Set<string>();

    for (const step of templateState.steps) {
      let subject: { type: string; ids?: string[]; id?: string; key?: string } | null = null;
      if (step.completion.type === 'Manual') {
        subject = step.completion.by;
      } else if (step.completion.type === 'Branch') {
        subject = step.completion.decider;
      }
      if (!subject) continue;

      let label: string;
      let displayName: string;
      switch (subject.type) {
        case 'Parameter':
          label = `param:${subject.key}`;
          displayName = subject.key || 'unknown';
          break;
        case 'Everyone':
          label = 'everyone';
          displayName = 'Everyone';
          break;
        case 'Team':
          label = `team:${subject.id}`;
          displayName = `Team ${subject.id}`;
          break;
        case 'Users':
          label = `users:${(subject.ids || []).sort().join(',')}`;
          displayName = `Users (${(subject.ids || []).length})`;
          break;
        default:
          continue;
      }

      if (!seen.has(label)) {
        seen.add(label);
        pools.push({ label, displayName });
      }
    }

    return pools;
  }, [templateState]);

  // Discover branch steps for pre-decisions UI
  const discoveredBranches = useMemo(() => {
    if (!templateState) return [];
    return templateState.steps
      .filter((s: any) => s.completion.type === 'Branch' && s.completion.options)
      .map((s: any) => ({
        stepId: s.id as string,
        title: s.title as string,
        options: s.completion.options as Record<string, string>,
      }));
  }, [templateState]);

  // Build the fuzzy schedule when requested
  const buildSchedule = useCallback(() => {
    if (!templateState) return;

    try {
      const poolConfig: [string, number][] = discoveredPools.map(p => [
        p.label,
        poolWorkerCounts[p.label] ?? 1,
      ]);

      const preDecisionPairs: [string, string[]][] = Object.entries(preDecisions)
        .filter(([, branches]) => branches.length > 0);

      const result = sched.build_workflow_fuzzy_schedule(
        templateState,
        poolConfig,
        preDecisionPairs,
        0.5,
        unitsPerDay,
      );

      if (result === null) {
        setScheduleError('Failed to build schedule (check console for details)');
        setScheduleResult(null);
      } else {
        setScheduleResult(result as FuzzyScheduleResult);
        setScheduleError(null);
      }
    } catch (err) {
      console.error('Failed to build fuzzy schedule:', err);
      setScheduleError(String(err));
      setScheduleResult(null);
    }
  }, [templateState, discoveredPools, poolWorkerCounts, unitsPerDay, preDecisions]);

  // Rebuild schedule when template or options change and panel is open
  useEffect(() => {
    if (showSchedule && templateState) {
      buildSchedule();
    }
  }, [showSchedule, templateState, poolWorkerCounts, unitsPerDay, preDecisions]);

  // Build critical path analysis
  const buildCriticalPath = useCallback(() => {
    if (!templateState) return;

    try {
      const poolConfig: [string, number][] = discoveredPools.map(p => [
        p.label,
        poolWorkerCounts[p.label] ?? 1,
      ]);

      const preDecisionPairs: [string, string[]][] = Object.entries(preDecisions)
        .filter(([, branches]) => branches.length > 0);

      const result = sched.analyze_workflow_critical_path(
        templateState,
        poolConfig,
        preDecisionPairs,
        unitsPerDay,
      );

      setCriticalPathData(result);
      setCriticalPathErrors(null);
    } catch (err) {
      // The WASM function throws a JSON string of CriticalPathMissingEstimate[]
      try {
        const parsed = JSON.parse(String(err)) as sched.CriticalPathMissingEstimate[];
        setCriticalPathErrors(parsed);
      } catch {
        setCriticalPathErrors([{ node_id: '0', step_id: null, reason: String(err) }]);
      }
      setCriticalPathData(null);
    }
  }, [templateState, discoveredPools, poolWorkerCounts, unitsPerDay, preDecisions]);

  // Recompute critical path when template changes and overlay is active
  useEffect(() => {
    if (showCriticalPath && templateState) {
      buildCriticalPath();
    } else if (!showCriticalPath) {
      setCriticalPathData(null);
      setCriticalPathErrors(null);
    }
  }, [showCriticalPath, templateState, poolWorkerCounts, unitsPerDay, preDecisions]);

  // Compute per-step critical path intensity and slack for graph nodes
  const { criticalPathNodeMap, criticalPathEdgeMap } = useMemo(() => {
    if (!criticalPathData) return { criticalPathNodeMap: null, criticalPathEdgeMap: null };
    const nodeToStep = criticalPathData.node_to_step;
    // Count how many of the 3 passes each step is on
    const stepCounts: Record<string, number> = {};
    const stepSlack: Record<string, { low: number; mode: number; high: number }> = {};
    // Count how many passes each edge (stepA→stepB) is on the critical path
    const edgeCounts: Record<string, number> = {};

    for (const [passKey, pass] of [['low', criticalPathData.low], ['mode', criticalPathData.mode], ['high', criticalPathData.high]] as const) {
      const cpSet = new Set(pass.critical_path);
      for (const nodeIdStr of Object.keys(pass.timings)) {
        const stepId = nodeToStep[nodeIdStr];
        if (!stepId) continue;
        if (cpSet.has(nodeIdStr)) {
          stepCounts[stepId] = (stepCounts[stepId] ?? 0) + 1;
        }
        const timing = pass.timings[nodeIdStr];
        if (!stepSlack[stepId]) stepSlack[stepId] = { low: Infinity, mode: Infinity, high: Infinity };
        stepSlack[stepId][passKey] = Math.min(stepSlack[stepId][passKey], timing.total_float);
      }

      // Build critical path edges: consecutive pairs in the ordered critical path
      const cpStepIds = pass.critical_path.map(nid => nodeToStep[nid]).filter(Boolean);
      for (let i = 0; i < cpStepIds.length - 1; i++) {
        const edgeKey = `${cpStepIds[i]}-${cpStepIds[i + 1]}`;
        edgeCounts[edgeKey] = (edgeCounts[edgeKey] ?? 0) + 1;
      }
    }

    const nodeMap: Record<string, { intensity: number; slack: { low: number; mode: number; high: number } | null }> = {};
    for (const stepId of Object.keys({ ...stepCounts, ...stepSlack })) {
      const count = stepCounts[stepId] ?? 0;
      const slack = stepSlack[stepId] ?? null;
      const cleanSlack = slack && (slack.low !== Infinity || slack.mode !== Infinity || slack.high !== Infinity)
        ? {
          low: slack.low === Infinity ? 0 : slack.low,
          mode: slack.mode === Infinity ? 0 : slack.mode,
          high: slack.high === Infinity ? 0 : slack.high,
        }
        : null;
      nodeMap[stepId] = { intensity: count / 3, slack: cleanSlack };
    }

    const edgeMap: Record<string, number> = {};
    for (const [key, count] of Object.entries(edgeCounts)) {
      edgeMap[key] = count / 3;
    }

    return { criticalPathNodeMap: nodeMap, criticalPathEdgeMap: edgeMap };
  }, [criticalPathData]);

  // Close "more" menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as HTMLElement)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Update graph when template state changes, auto-layout when steps are added
  useEffect(() => {
    if (templateState) {
      convertStateToGraph(templateState);
      const count = templateState.steps.length;
      if (prevStepCount.current !== null && count > prevStepCount.current) {
        setLayoutGeneration(g => g + 1);
      }
      prevStepCount.current = count;
    }
  }, [templateState]);

  // Apply critical path overlay to nodes
  useEffect(() => {
    setNodes(prev => prev.map(node => ({
      ...node,
      data: {
        ...node.data,
        criticalPathIntensity: criticalPathNodeMap?.[node.id]?.intensity ?? 0,
        slackDays: criticalPathNodeMap?.[node.id]?.slack ?? null,
      },
    })));
  }, [criticalPathNodeMap]);

  // Apply critical path overlay to edges
  useEffect(() => {
    setEdges(prev => prev.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        criticalPathIntensity: criticalPathEdgeMap?.[edge.id] ?? 0,
      },
    })));
  }, [criticalPathEdgeMap]);

  // Step form
  const stepFormik = useFormik({
    initialValues: {
      title: '',
      description: '',
      condition: {
        type: 'Immediate' as WorkflowStepConditionFormData['type'],
      } as WorkflowStepConditionFormData,
      inclusionCondition: null as sched.StepInclusionCondition | null,
    },
    validationSchema: toFormikValidationSchema(stepSchema),
    onSubmit: async (values) => {
      await handleSaveStep(values);
    },
  });

  const convertStateToGraph = (state: sched.WorkflowTemplateState) => {
    // Convert steps to nodes
    const newNodes: Node<WorkflowGraphNodeData>[] = state.steps.map((step) => ({
      id: step.id,
      // `default` leads to weird default styling issues.
      // bypass with a custom type.
      type: 'workflowStep',
      position: { x: 0, y: 0 }, // Initial position, will be overridden by layout
      width: 200,
      data: {
        step: step,
        displayContext: getNodeDisplayContext(userProfilesResponse.result || [], step, undefined)
      },
      draggable: true,
    }));

    // Convert links to edges with custom deletable edge type
    const newEdges: Edge[] = state.links.map((link) => ({
      id: `${link.source_step_id}-${link.target_step_id}`,
      source: link.source_step_id,
      sourceHandle: link.source_step_branch_id || undefined,
      target: link.target_step_id,
      type: 'deletable',
      animated: true,
      data: {
        onDelete: handleDeleteLink,
        linkId: link.id, // Pass the actual link ID for direct deletion
        sourceStepId: link.source_step_id,
        targetStepId: link.target_step_id,
      },
    }));

    setNodes(newNodes);
    setEdges(newEdges);
  };



  const handleSaveStep = async (values: { title: string; description: string; condition: WorkflowStepConditionFormData; inclusionCondition: sched.StepInclusionCondition | null }) => {
    if (!client || !token || !templateId) return;

    try {
      const stepId = modalConfig?.type === 'edit' ? modalConfig.stepId : crypto.randomUUID();

      const stepCondition = formDataToWorkflowStepCondition(values.condition);

      // Preserve hooks from the existing step if editing
      const existingStep = modalConfig?.type === 'edit' && templateState
        ? templateState.steps.find(s => s.id === stepId)
        : null;

      const step: sched.WorkflowTemplateStep = {
        id: stepId,
        title: values.title,
        description: values.description,
        completion: stepCondition,
        hooks: existingStep?.hooks ?? [],
        condition: values.inclusionCondition ?? undefined,
      };

      // TODO consider more delicate/targeted updates with UpdateStep
      const eventData: sched.WorkflowTemplateEventData = modalConfig?.type === 'edit'
        ? { type: 'ReplaceStep', step }
        : { type: 'AddStep', step };

      const eventRequest: sched.CreateWorkflowTemplateEventRequest = {
        data: eventData,
      };

      await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, eventRequest);

      // Refresh the events to rebuild state
      eventsResponse.execute();

      setModalConfig(null);
      stepFormik.resetForm();

      enqueueSnackbar(modalConfig?.type === 'edit' ? 'Step updated successfully' : 'Step added successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to save step:', err);
      enqueueSnackbar('Failed to save step', { variant: 'error' });
    }
  };

  /** Build RemoveLink + RemoveStep events for a step and all its connected links. */
  const buildRemoveStepEvents = (stepId: string): sched.WorkflowTemplateEventData[] => {
    const events: sched.WorkflowTemplateEventData[] = [];
    // Remove all links touching this step first
    for (const link of templateState?.links ?? []) {
      if (link.source_step_id === stepId || link.target_step_id === stepId) {
        events.push({ type: 'RemoveLink', link_id: link.id });
      }
    }
    events.push({ type: 'RemoveStep', step_id: stepId });
    return events;
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!client || !token || !templateId) return;

    const step = templateState?.steps.find(s => s.id === stepId);

    if (!window.confirm(`Are you sure you want to delete "${step?.title}"?`)) {
      return;
    }

    try {
      const events = buildRemoveStepEvents(stepId);
      await sched.WrappedApiClient.batch_create_workflow_template_events(
        client, token, templateId, { events },
      );

      // Refresh the events to rebuild state
      eventsResponse.execute();

      enqueueSnackbar('Step deleted successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to delete step:', err);
      enqueueSnackbar('Failed to delete step', { variant: 'error' });
    }
  };

  // Detach: create a standalone item in the local database, then swap the
  // workflow step to ExternalDependency type referencing the new item.
  //
  // The step keeps its ID and downstream links. The free node is created
  // with the same UUID as the step for easy recovery/debugging. Upstream
  // links are removed since ExternalDependency steps must be source nodes.
  //
  // ⚠ ATOMICITY GAP: The local database write (CreateItem) and the remote
  // template write (ReplaceStep) are not atomic. We create locally first so
  // the dangerous failure mode (data loss) is avoided — worst case is a
  // free node exists without the template step being swapped.
  const handleDetachStep = async (stepId: string) => {
    if (!client || !token || !templateId) return;

    const step = templateState?.steps.find(s => s.id === stepId);
    if (!step) return;

    const condType = step.completion.type;
    const detachable = condType === 'Manual' || condType === 'Branch';
    if (!detachable) return;

    const itemLabel = condType === 'Manual' ? 'task' : 'decision';
    if (!window.confirm(
      `Detach "${step.title}"?\n\nThis will create a standalone ${itemLabel} in your workspace and convert the step to an external dependency reference.`
    )) return;

    try {
      // Use the step's own UUID as the free node's ID for traceability
      const itemId = stepId;

      // Step 1: Create the item in the local database
      if (condType === 'Manual') {
        const nativeTask: sched.NativeTask = {
          title: step.title,
          description: step.description || null,
          estimates: [],
          project_id: null,
          tags: [],
          complete: false,
        };
        const command: sched.DatabaseCommand = {
          type: 'CreateItem',
          id: itemId,
          item: { type: 'Task', value: { Native: nativeTask } },
        };
        await applyCommand(command);
      } else {
        // Branch → Decision
        const completion = step.completion as sched.WorkflowStepCondition & { type: 'Branch' };
        const decisionNode: sched.DecisionNode = {
          title: step.title,
          choices: Object.values(completion.options),
          exclusive: !completion.multi_select,
        };
        const command: sched.DatabaseCommand = {
          type: 'CreateItem',
          id: itemId,
          item: { type: 'Decision', value: decisionNode },
        };
        await applyCommand(command);
      }

      // Step 2: Swap the step to ExternalDependency in the template.
      // Remove upstream links (ExternalDependency must be a source node),
      // but keep downstream links so dependent steps still reference it.
      const events: sched.WorkflowTemplateEventData[] = [];
      for (const link of templateState?.links ?? []) {
        if (link.target_step_id === stepId) {
          events.push({ type: 'RemoveLink', link_id: link.id });
        }
      }

      // Build the DependencyNodeId for the ExternalDependency
      const nodeRef = condType === 'Manual'
        ? { Task: itemId }
        : { Decision: itemId };

      events.push({
        type: 'ReplaceStep',
        step: {
          id: stepId,
          title: step.title,
          description: step.description,
          completion: { type: 'ExternalDependency', node: nodeRef } as any,
          hooks: [],
          condition: undefined,
        },
      });

      await sched.WrappedApiClient.batch_create_workflow_template_events(
        client, token, templateId, { events },
      );

      eventsResponse.execute();
      setModalConfig(null);
      enqueueSnackbar(`Step detached — ${itemLabel} "${step.title}" created as external dependency`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to detach step:', err);
      enqueueSnackbar('Failed to detach step. The item may have been created — check your task list.', { variant: 'error' });
    }
  };

  const handleAttachNode = async (nodeId: string, nodeType: 'Task' | 'Decision', title: string) => {
    if (!client || !token || !templateId) return;

    // Check if this node is already attached in the template
    const alreadyAttached = templateState?.steps.some(s => {
      const c = s.completion as any;
      return c.type === 'ExternalDependency' && (
        (c.node?.Task === nodeId) || (c.node?.Decision === nodeId)
      );
    });
    if (alreadyAttached) {
      enqueueSnackbar('This node is already attached to the template', { variant: 'warning' });
      return;
    }

    try {
      const nodeRef = nodeType === 'Task' ? { Task: nodeId } : { Decision: nodeId };
      const step: sched.WorkflowTemplateStep = {
        id: uuidv4(),
        title,
        description: '',
        completion: { type: 'ExternalDependency', node: nodeRef } as any,
        hooks: [],
        condition: undefined,
      };

      const request: sched.CreateWorkflowTemplateEventRequest = {
        data: { type: 'AddStep', step },
      };
      await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
      eventsResponse.execute();
      setShowAttachModal(false);
      enqueueSnackbar(`Attached "${title}" as external dependency`, { variant: 'success' });
    } catch (err) {
      console.error('Failed to attach node:', err);
      enqueueSnackbar('Failed to attach node', { variant: 'error' });
    }
  };

  const handleAddStep = () => {
    setModalConfig({ type: 'create' });
    stepFormik.resetForm();
  };

  const handleEditStep = (step: sched.WorkflowTemplateStep) => {
    stepFormik.setValues({
      title: step.title,
      description: step.description,
      condition: workflowStepConditionToFormData(step.completion),
      inclusionCondition: step.condition ?? null,
    });

    setModalConfig({ type: 'edit', stepId: step.id });
  };

  const handleDeleteLink = async (edgeId: string, linkData?: { linkId: string; sourceStepId: string; targetStepId: string }) => {
    if (!client || !token || !templateId) return;

    try {
      let linkId: string;

      if (linkData?.linkId) {
        // Use the direct link ID passed from edge data
        linkId = linkData.linkId;
        console.log('Deleting link with direct ID:', linkId);
      } else {
        // Fallback: parse edge ID and find the link (legacy approach)
        const [sourceStepId, targetStepId] = edgeId.split('-');
        console.log('Deleting link via parsing:', { edgeId, sourceStepId, targetStepId });

        const linkToDelete = templateState?.links.find(
          link => link.source_step_id === sourceStepId && link.target_step_id === targetStepId
        );

        if (!linkToDelete) {
          console.error('Link not found. Available links:', templateState?.links?.map(l => ({
            id: l.id,
            source: l.source_step_id,
            target: l.target_step_id
          })));
          enqueueSnackbar(`Link not found: ${sourceStepId} → ${targetStepId}`, { variant: 'error' });
          return;
        }

        linkId = linkToDelete.id;
      }

      const eventData: sched.WorkflowTemplateEventData = {
        type: 'RemoveLink',
        link_id: linkId,
      };

      const eventRequest: sched.CreateWorkflowTemplateEventRequest = {
        data: eventData,
      };

      await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, eventRequest);

      // Refresh the events to rebuild state
      eventsResponse.execute();

      enqueueSnackbar('Link deleted successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to delete link:', err);
      enqueueSnackbar('Failed to delete link', { variant: 'error' });
    }
  };

  // const onConnectEnd = useCallback((_, connectionState: ConnectionState) => {
  //   if (!connectionState.isValid) {
  //     handleAddStep();
  //   }
  // }, []);

  const onConnect = useCallback(async (params: Connection) => {
    if (!client || !token || !templateId || !params.source || !params.target) return;

    console.log("onConnect", params);

    try {
      const link: sched.WorkflowLink = {
        id: crypto.randomUUID(),
        source_step_id: params.source,
        source_step_branch_id: params.sourceHandle,
        target_step_id: params.target,
      };

      const eventData: sched.WorkflowTemplateEventData = {
        type: 'AddLink',
        link,
      };

      const eventRequest: sched.CreateWorkflowTemplateEventRequest = {
        data: eventData,
      };

      await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, eventRequest);

      // Refresh the events to rebuild state (this will trigger layout recalculation)
      eventsResponse.execute();

      enqueueSnackbar('Link added successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to add link:', err);
      enqueueSnackbar('Failed to add link', { variant: 'error' });
    }
  }, [client, token, templateId]);

  const handleStartWorkflow = () => {
    navigate(buildUrl({ kind: 'workflow-instances-list', templateFilter: templateId ?? undefined }));
  };

  // ── Import / Export ──────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!templateState) return;
    const json = JSON.stringify(templateState, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${templateResponse.result?.title ?? 'workflow'}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [templateState, templateResponse.result]);

  if (templateResponse.error || eventsResponse.error) {
    return <ErrorAlert message={templateResponse.error?.message || eventsResponse.error?.message || 'Failed to load template'} />;
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="flex-none bg-cm-bg-surface border-b border-cm-border-subtle p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <EditableTitle
              title={templateResponse.result?.title || 'Untitled'}
              size="sm"
              onTitleChange={async (newTitle) => {
                if (!client || !token || !templateId) return;
                // Update the non-versioned template title
                await sched.WrappedApiClient.update_workflow_template(
                  client, token, templateId,
                  { title: newTitle } as any
                );
                // Also emit a versioned SetTitle event
                await sched.WrappedApiClient.create_workflow_template_event(
                  client, token, templateId,
                  { data: { type: 'SetTitle', title: newTitle } } as any
                );
                templateResponse.execute();
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-cm-border-subtle overflow-hidden">
              <button
                onClick={() => setViewMode('graph')}
                className={`px-2.5 py-1.5 text-xs font-medium ${viewMode === 'graph' ? 'bg-blue-600 text-white' : 'bg-cm-bg-surface text-cm-text-primary hover:bg-cm-bg-hover'}`}
              >Graph</button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-2.5 py-1.5 text-xs font-medium border-l border-cm-border-subtle ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-cm-bg-surface text-cm-text-primary hover:bg-cm-bg-hover'}`}
              >
                <FiGrid className="w-3.5 h-3.5 inline mr-1" />Table
              </button>
            </div>
            <div className="relative" ref={moreMenuRef}>
              <Button color="gray" onClick={() => setShowMoreMenu(v => !v)} className="flex items-center gap-2">
                <FiChevronDown className="w-4 h-4" />
              </Button>
              {showMoreMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-cm-bg-surface rounded-lg shadow-lg border border-cm-border-subtle z-50 py-1">
                  <button onClick={() => { handleAddStep(); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                    <FiPlus className="w-4 h-4" /> Add Step
                  </button>
                  <button onClick={() => { setShowAttachModal(true); setAttachSearch(''); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                    <FiExternalLink className="w-4 h-4" /> Attach
                  </button>
                  {templateState && (
                    <>
                      <div className="border-t border-cm-border-subtle my-1" />
                      <button onClick={() => { setShowCriticalPath(!showCriticalPath); setShowMoreMenu(false); }} className={`w-full text-left px-4 py-2 text-sm hover:bg-cm-bg-hover flex items-center gap-2 ${showCriticalPath ? 'text-red-600' : 'text-cm-text-primary'}`}>
                        <FiTarget className="w-4 h-4" /> {showCriticalPath ? 'Hide' : 'Show'} Critical Path
                      </button>
                      <button onClick={() => { setShowSchedule(!showSchedule); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                        <FiBarChart2 className="w-4 h-4" /> Schedule
                      </button>
                      <div className="border-t border-cm-border-subtle my-1" />
                    </>
                  )}
                  <button onClick={() => { setLayoutGeneration(g => g + 1); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                    <FiLayout className="w-4 h-4" /> Re-layout
                  </button>
                  <button onClick={() => { handleExport(); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                    <FiDownload className="w-4 h-4" /> Export
                  </button>
                  <button onClick={() => { handleStartWorkflow(); setShowMoreMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-cm-text-primary hover:bg-cm-bg-hover flex items-center gap-2">
                    <FiPlay className="w-4 h-4" /> Launch
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Editor area */}
      <div className="flex-1 relative">
        {/* Template Loading Indicator */}
        {(templateResponse.loading || eventsResponse.loading) && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className="flex items-center gap-2 bg-cm-bg-surface px-3 py-2 rounded-lg shadow-md border border-cm-border-subtle">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-cm-text-primary">Loading...</span>
            </div>
          </div>
        )}

        {/* Critical path warnings overlay */}
        {showCriticalPath && criticalPathErrors && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 w-[90%] max-w-xl">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg shadow-md text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
                Critical path analysis unavailable — missing information:
              </p>
              <ul className="space-y-0.5 text-amber-700 dark:text-amber-400">
                {criticalPathErrors.map((e, i) => {
                  const step = e.step_id && templateState
                    ? templateState.steps.find((s: any) => s.id === e.step_id)
                    : null;
                  return (
                    <li key={i}>
                      {step ? (
                        <button
                          type="button"
                          className="underline hover:text-amber-900 dark:hover:text-amber-200 cursor-pointer"
                          onClick={() => handleEditStep(step)}
                        >
                          • {e.reason}
                        </button>
                      ) : (
                        <>• {e.reason}</>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                CPM requires all tasks to have work estimates and all decisions to be configured.
              </p>
            </div>
          </div>
        )}

        {/* Critical path legend overlay */}
        {showCriticalPath && criticalPathData && (
          <div className="absolute top-2 right-2 z-20">
            <div className="p-2 bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-md text-xs space-y-1">
              <p className="font-medium text-cm-text-primary">Critical Path</p>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm ring-2 ring-red-500 bg-cm-bg-surface" />
                <span className="text-cm-text-secondary">All 3 estimates</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm ring-2 ring-red-400/70 bg-cm-bg-surface" />
                <span className="text-cm-text-secondary">2 of 3 estimates</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm ring-2 ring-red-300/50 bg-cm-bg-surface" />
                <span className="text-cm-text-secondary">1 of 3 estimates</span>
              </div>
              <p className="text-[10px] text-cm-text-muted pt-0.5 border-t border-cm-border-subtle">
                Duration-only analysis (ignores resourcing).
                <br />Hover nodes for slack values.
              </p>
            </div>
          </div>
        )}

        {viewMode === 'graph' ? (
          <WorkflowGraph
            nodes={nodes}
            edges={edges}
            onConnect={onConnect}
            nodesConnectable={true}
            elementsSelectable={true}
            layoutGeneration={layoutGeneration}
            onNodeClick={(_, node) => {
              if (node.data && 'step' in node.data) {
                handleEditStep((node.data as WorkflowGraphNodeData).step);
              }
            }}
          />
        ) : templateState ? (
          <WorkflowTableView
            templateState={templateState}
            onEditStep={handleEditStep}
            onDeleteStep={handleDeleteStep}
            onSaveTitle={async (stepId, title) => {
              if (!client || !token || !templateId) return;
              const step = templateState.steps.find(s => s.id === stepId);
              if (!step) return;
              const updated: sched.WorkflowTemplateStep = { ...step, title };
              const request: sched.CreateWorkflowTemplateEventRequest = {
                data: { type: 'ReplaceStep', step: updated },
              };
              await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
              eventsResponse.execute();
            }}
            onSaveEstimate={async (stepId, estimate) => {
              if (!client || !token || !templateId) return;
              const step = templateState.steps.find(s => s.id === stepId);
              if (!step || step.completion.type !== 'Manual') return;
              const updated: sched.WorkflowTemplateStep = {
                ...step,
                completion: { ...step.completion, work_estimate: estimate },
              };
              const request: sched.CreateWorkflowTemplateEventRequest = {
                data: { type: 'ReplaceStep', step: updated },
              };
              await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
              eventsResponse.execute();
            }}
            onAddDependency={async (targetStepId, sourceStepId) => {
              if (!client || !token || !templateId) return;
              const link: sched.WorkflowLink = {
                id: crypto.randomUUID(),
                source_step_id: sourceStepId,
                source_step_branch_id: null,
                target_step_id: targetStepId,
              };
              const request: sched.CreateWorkflowTemplateEventRequest = {
                data: { type: 'AddLink', link },
              };
              await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
              eventsResponse.execute();
            }}
            onRemoveDependency={async (targetStepId, sourceStepId) => {
              if (!client || !token || !templateId) return;
              const link = templateState.links.find(
                l => l.source_step_id === sourceStepId && l.target_step_id === targetStepId
              );
              if (!link) return;
              const request: sched.CreateWorkflowTemplateEventRequest = {
                data: { type: 'RemoveLink', link_id: link.id },
              };
              await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
              eventsResponse.execute();
            }}
          />
        ) : null}
      </div>

      {/* Schedule Modal */}
      <Modal show={showSchedule} onClose={() => setShowSchedule(false)} size="7xl">
        <ModalHeader>Schedule Preview</ModalHeader>
        <ModalBody>
          {/* Schedule options */}
          <div className="mb-4 space-y-2">
            {/* Units per day — always visible */}
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-1.5 text-sm">
                <span className="font-medium text-cm-text-primary">Work units per day:</span>
                <input
                  type="number"
                  min={0.1}
                  max={24}
                  step={0.5}
                  value={unitsPerDay}
                  onChange={e => {
                    const val = Math.max(0.1, parseFloat(e.target.value) || 1);
                    setUnitsPerDay(val);
                  }}
                  className="w-16 rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1 text-sm text-center"
                />
              </label>
              <span className="text-xs text-cm-text-muted">
                If estimates are in hours, set to the number of productive hours per day (e.g. 6)
              </span>
            </div>

            {/* Pool worker counts — collapsible */}
            {discoveredPools.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-cm-text-primary select-none">
                  Workers per pool ({discoveredPools.length} pools)
                </summary>
                <div className="mt-2 grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                  {discoveredPools.map(pool => (
                    <label key={pool.label} className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-cm-bg-hover/50">
                      <span className="text-cm-text-secondary truncate">{pool.displayName}</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={poolWorkerCounts[pool.label] ?? 1}
                        onChange={e => {
                          const val = Math.max(1, parseInt(e.target.value) || 1);
                          setPoolWorkerCounts(prev => ({ ...prev, [pool.label]: val }));
                        }}
                        className="w-14 rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1 text-sm text-center flex-shrink-0"
                      />
                    </label>
                  ))}
                </div>
              </details>
            )}

            {/* Pre-decisions for branch steps — collapsible */}
            {discoveredBranches.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-cm-text-primary select-none">
                  Branch decisions ({discoveredBranches.length} decisions)
                </summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {discoveredBranches.map(branch => (
                    <label key={branch.stepId} className="flex items-center gap-1.5">
                      <span className="text-cm-text-secondary truncate">{branch.title}</span>
                      <select
                        value={preDecisions[branch.stepId]?.[0] ?? ''}
                        onChange={e => {
                          const val = e.target.value;
                          setPreDecisions(prev => ({
                            ...prev,
                            [branch.stepId]: val ? [val] : [],
                          }));
                        }}
                        className="rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1 text-sm"
                      >
                        <option value="">All paths (undecided)</option>
                        {Object.entries(branch.options).map(([optId, optLabel]) => (
                          <option key={optId} value={optId}>{optLabel}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </details>
            )}
          </div>

          {/* Schedule result */}
          {scheduleError && (
            <div className="text-sm text-red-500 mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded">{scheduleError}</div>
          )}
          {scheduleResult && Object.keys(scheduleResult.schedule.unschedulable).length > 0 && (
            <UnschedulablePanel
              unschedulable={scheduleResult.schedule.unschedulable}
              meta={scheduleResult.meta}
              templateState={templateState}
              onFixEstimate={async (stepId, estimate) => {
                if (!client || !token || !templateId || !templateState) return;
                const step = templateState.steps.find((s: any) => s.id === stepId);
                if (!step || step.completion.type !== 'Manual') return;
                const updated = {
                  ...step,
                  completion: { ...step.completion, work_estimate: estimate },
                };
                const request: sched.CreateWorkflowTemplateEventRequest = {
                  data: { type: 'ReplaceStep' as const, step: updated },
                };
                await sched.WrappedApiClient.create_workflow_template_event(client, token, templateId, request);
                eventsResponse.execute();
              }}
              onEditStep={(stepId) => {
                const step = templateState?.steps.find((s: any) => s.id === stepId);
                if (step) handleEditStep(step);
              }}
              onPreDecide={(stepId, optionId) => {
                setPreDecisions(prev => ({
                  ...prev,
                  [stepId]: optionId ? [optionId] : [],
                }));
              }}
              discoveredBranches={discoveredBranches}
            />
          )}
          {scheduleResult && Object.keys(scheduleResult.schedule.items).length > 0 ? (
            <FuzzyGanttChart
              result={scheduleResult}
              onStepClick={(stepId) => {
                const step = templateState?.steps.find(s => s.id === stepId);
                if (step) handleEditStep(step);
              }}
            />
          ) : scheduleResult && Object.keys(scheduleResult.schedule.items).length === 0 && !scheduleError ? (
            <div className="text-sm text-cm-text-muted p-8 text-center">
              No items could be scheduled. Check that your workflow has Task steps with work assignments.
            </div>
          ) : !scheduleError ? (
            <div className="text-sm text-cm-text-muted p-8 text-center">Building schedule...</div>
          ) : null}
        </ModalBody>
      </Modal>

      {/* Step Modal */}
      <Modal show={modalConfig !== null} onClose={() => setModalConfig(null)}>
        <ModalHeader>
          {modalConfig?.type === 'edit' ? 'Edit Step' : 'Add Step'}
        </ModalHeader>
        <ModalBody>
          {stepFormik.submitCount > 0 && Object.keys(stepFormik.errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-1">Please fix the following:</p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                {(() => {
                  const messages: string[] = [];
                  const collect = (obj: any, path = '') => {
                    if (typeof obj === 'string') messages.push(obj);
                    else if (obj && typeof obj === 'object') {
                      for (const [k, v] of Object.entries(obj)) collect(v, path ? `${path}.${k}` : k);
                    }
                  };
                  collect(stepFormik.errors);
                  return messages.map((msg, i) => <li key={i}>{msg}</li>);
                })()}
              </ul>
            </div>
          )}
          <form onSubmit={stepFormik.handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <TextInput
                id="title"
                name="title"
                value={stepFormik.values.title}
                onChange={stepFormik.handleChange}
                onBlur={stepFormik.handleBlur}
                placeholder="Enter step title"
                color={stepFormik.touched.title && stepFormik.errors.title ? 'failure' : undefined}
              />
              {stepFormik.touched.title && stepFormik.errors.title && (
                <span className="text-red-500 text-sm">{stepFormik.errors.title}</span>
              )}
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={stepFormik.values.description}
                onChange={stepFormik.handleChange}
                onBlur={stepFormik.handleBlur}
                placeholder="Enter step description"
                rows={3}
              />
              {stepFormik.touched.description && stepFormik.errors.description && (
                <span className="text-red-500 text-sm">{stepFormik.errors.description}</span>
              )}
            </div>
            <WorkflowStepConditionForm
              value={stepFormik.values.condition}
              onChange={(condition) => stepFormik.setFieldValue('condition', condition)}
              workspaceId={workspaceId}
              errors={stepFormik.errors.condition as any}
              touched={stepFormik.touched.condition as any}
              existingParameters={(() => {
                if (!templateState) return [];
                const keys = new Set<string>();
                for (const step of templateState.steps) {
                  const c = step.completion;
                  if (c.type === 'Manual' && c.by.type === 'Parameter') keys.add(c.by.key);
                  if (c.type === 'Branch' && c.decider.type === 'Parameter') keys.add((c.decider as any).key);
                  if (step.condition?.type === 'BoolParameter') keys.add(step.condition.key);
                }
                return [...keys].sort();
              })()}
            />

            {/* Inclusion Condition */}
            <div className="space-y-2 p-3 border border-cm-border-subtle rounded-md">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Inclusion Condition</Label>
                <Checkbox
                  id="has-inclusion-condition"
                  checked={stepFormik.values.inclusionCondition !== null}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    stepFormik.setFieldValue(
                      'inclusionCondition',
                      e.target.checked ? { type: 'BoolParameter', key: '', negate: false } : null,
                    );
                  }}
                />
              </div>
              <p className="text-xs text-cm-text-muted">
                When enabled, this step is only included when a boolean parameter matches.
              </p>
              {stepFormik.values.inclusionCondition && (
                <div className="space-y-2 pt-1">
                  <div>
                    <Label htmlFor="inclusion-param-key">Parameter key</Label>
                    <TextInput
                      id="inclusion-param-key"
                      value={stepFormik.values.inclusionCondition.key}
                      onChange={(e) =>
                        stepFormik.setFieldValue('inclusionCondition', {
                          ...stepFormik.values.inclusionCondition,
                          key: e.target.value,
                        })
                      }
                      placeholder="e.g. include_monitoring"
                      list="inclusion-param-suggestions"
                    />
                    <datalist id="inclusion-param-suggestions">
                      {(() => {
                        if (!templateState) return null;
                        const keys = new Set<string>();
                        for (const step of templateState.steps) {
                          if (step.condition?.type === 'BoolParameter') keys.add(step.condition.key);
                        }
                        return [...keys].sort().map(k => <option key={k} value={k} />);
                      })()}
                    </datalist>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-cm-text-primary">
                    <Checkbox
                      checked={stepFormik.values.inclusionCondition.negate ?? false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        stepFormik.setFieldValue('inclusionCondition', {
                          ...stepFormik.values.inclusionCondition,
                          negate: e.target.checked,
                        })
                      }
                    />
                    Negate (include when parameter is false or absent)
                  </label>
                </div>
              )}
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between w-full">
            <div className="flex gap-2">
              {modalConfig?.type === 'edit' && (() => {
                const editStep = templateState?.steps.find(s => s.id === modalConfig.stepId);
                const canDetach = editStep && (editStep.completion.type === 'Manual' || editStep.completion.type === 'Branch');
                return (
                  <>
                    <Button
                      color="failure"
                      onClick={() => {
                        setModalConfig(null);
                        handleDeleteStep(modalConfig.stepId);
                      }}
                      className="flex items-center gap-2"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </Button>
                    {canDetach && (
                      <Button
                        color="light"
                        onClick={() => handleDetachStep(modalConfig.stepId)}
                        className="flex items-center gap-2"
                        title="Create a standalone item from this step and remove it from the template"
                      >
                        <FiExternalLink className="w-4 h-4" />
                        Detach
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="flex gap-2">
              <Button color="gray" onClick={() => setModalConfig(null)}>
                Cancel
              </Button>
              <Button onClick={() => {
                stepFormik.validateForm().then(errors => {
                  if (Object.keys(errors).length > 0) {
                    // Touch all fields so inline errors become visible
                    const touchAll = (obj: Record<string, any>, prefix = ''): Record<string, any> => {
                      const result: Record<string, any> = {};
                      for (const key of Object.keys(obj)) {
                        const path = prefix ? `${prefix}.${key}` : key;
                        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                          result[key] = touchAll(obj[key], path);
                        } else {
                          result[key] = true;
                        }
                      }
                      return result;
                    };
                    stepFormik.setTouched(touchAll(errors));
                  } else {
                    stepFormik.submitForm();
                  }
                });
              }}>
                {modalConfig?.type === 'edit' ? 'Update Step' : 'Add Step'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Attach Node Modal */}
      <Modal show={showAttachModal} onClose={() => setShowAttachModal(false)}>
        <ModalHeader>Attach External Node</ModalHeader>
        <ModalBody>
          <p className="text-sm text-cm-text-muted mb-3">
            Select a task or decision from your workspace to attach as an external dependency in this template.
          </p>
          <TextInput
            placeholder="Filter by title..."
            value={attachSearch}
            onChange={e => setAttachSearch(e.target.value)}
            className="mb-3"
          />
          <div className="max-h-72 overflow-y-auto space-y-1">
            {(() => {
              const tasks = database?.tasks() ?? {};
              const decisions = database?.decisions() ?? {};
              const searchLower = attachSearch.toLowerCase();

              const items: { id: string; title: string; type: 'Task' | 'Decision' }[] = [];
              for (const [id, task] of Object.entries(tasks)) {
                const t = task as any;
                if (!searchLower || (t.title ?? '').toLowerCase().includes(searchLower)) {
                  items.push({ id, title: t.title ?? 'Untitled task', type: 'Task' });
                }
              }
              for (const [id, decision] of Object.entries(decisions)) {
                const d = decision as any;
                if (!searchLower || (d.title ?? '').toLowerCase().includes(searchLower)) {
                  items.push({ id, title: d.title ?? 'Untitled decision', type: 'Decision' });
                }
              }

              if (items.length === 0) {
                return <p className="text-sm text-cm-text-muted text-center py-4">No matching items found.</p>;
              }

              return items.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleAttachNode(item.id, item.type, item.title)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-cm-bg-hover flex items-center justify-between group"
                >
                  <span className="text-sm text-cm-text-primary truncate">{item.title}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    item.type === 'Task'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30'
                      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                  }`}>
                    {item.type}
                  </span>
                </button>
              ));
            })()}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={() => setShowAttachModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default WorkflowTemplateEditor;
